import React, { useState } from 'react';
import { Form, Button, Accordion } from 'react-bootstrap';

/**
 * DynamicForm renders an interactive form based on agent-provided schema.
 * Used to efficiently gather multiple specifications at once.
 *
 * Each field's label (or name) is used as the key in the submitted JSON.
 * This means submission produces { "Toilet Type": "One-piece", "Color": "White" }
 * — keys are human-readable with no separate label mapping needed.
 *
 * Props:
 * - formData: { title, description, work_item_id?, sections: [{ name, items: [{ name?, label, type, options, placeholder, default_value, spec_key?, spec_label? }] }] }
 * - onSubmit: (formValues, formMetadata) => void
 * - disabled: boolean
 * - submittedValues: object | null - Previously submitted values for persistence
 */
function DynamicForm({ formData, onSubmit, disabled, submittedValues }) {
  // Build a flat list of field keys for all form items.
  // Each field's key is item.name || item.label. If two fields produce the same
  // key, they are disambiguated by prefixing with the section name.
  const buildFieldKeys = () => {
    const keys = [];
    if (!formData || !formData.sections) return keys;

    // First pass: collect raw keys and detect duplicates
    const rawEntries = [];
    const keyCounts = {};
    formData.sections.forEach((section, sIdx) => {
      section.items.forEach((item, iIdx) => {
        const rawKey = item.label || item.name || `Field ${sIdx + 1}.${iIdx + 1}`;
        rawEntries.push({ sIdx, iIdx, item, section, rawKey });
        keyCounts[rawKey] = (keyCounts[rawKey] || 0) + 1;
      });
    });

    // Second pass: prefix duplicates with section name
    rawEntries.forEach((entry) => {
      const fieldKey = keyCounts[entry.rawKey] > 1
        ? `${entry.section.name}: ${entry.rawKey}`
        : entry.rawKey;
      keys.push({ sIdx: entry.sIdx, iIdx: entry.iIdx, item: entry.item, fieldKey });
    });

    return keys;
  };

  // Build a lookup from (sIdx, iIdx) → fieldKey for use in render and handlers
  const fieldKeyMap = () => {
    const map = {};
    buildFieldKeys().forEach(({ sIdx, iIdx, fieldKey }) => {
      map[`${sIdx}_${iIdx}`] = fieldKey;
    });
    return map;
  };

  // Initialize form values keyed by fieldKey
  const initializeValues = () => {
    const vals = {};
    const fields = buildFieldKeys();
    fields.forEach(({ item, fieldKey }) => {
      // Try fieldKey first, then fall back to item.id/item.name for backward compat with persisted data
      const persisted = submittedValues && (
        submittedValues[fieldKey] || submittedValues[item.id] || submittedValues[item.name]
      );
      vals[fieldKey] = persisted || item.default_value || '';
    });
    return vals;
  };

  const [values, setValues] = useState(initializeValues);
  const [otherTexts, setOtherTexts] = useState({});
  const [submitted, setSubmitted] = useState(!!submittedValues);
  const keyMap = fieldKeyMap();

  const handleChange = (fieldKey, value) => {
    setValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleOtherTextChange = (fieldKey, text) => {
    setOtherTexts((prev) => ({
      ...prev,
      [fieldKey]: text,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (disabled || submitted) return;

    const fields = buildFieldKeys();

    // Collect filled values — keys are already human-readable labels
    // Resolve "Other" selections to their free-text value
    const filledValues = {};
    let hasValues = false;
    fields.forEach(({ fieldKey }) => {
      let value = values[fieldKey];
      if (value === '__other__' && otherTexts[fieldKey]) {
        value = otherTexts[fieldKey].trim();
      }
      if (value && value !== '__other__' && typeof value === 'string' && value.trim() !== '') {
        filledValues[fieldKey] = value;
        hasValues = true;
      } else if (value && typeof value !== 'string') {
        filledValues[fieldKey] = value;
        hasValues = true;
      }
    });

    // Build metadata for auto-spec mapping and label resolution
    const formMetadata = {
      work_item_id: formData.work_item_id || null,
      specMappings: {},
      labelMap: {},
    };

    fields.forEach(({ item, fieldKey }) => {
      formMetadata.labelMap[fieldKey] = item.label || fieldKey;
      if (item.spec_key) {
        formMetadata.specMappings[fieldKey] = {
          key: item.spec_key,
          label: item.spec_label || item.label,
        };
      }
    });

    if (!hasValues) {
      onSubmit({ _empty: true, message: 'No changes selected' }, formMetadata);
    } else {
      onSubmit(filledValues, formMetadata);
    }
    setSubmitted(true);
  };

  const renderInput = (item, fieldKey) => {
    const value = values[fieldKey] || '';
    const isDisabled = disabled || submitted;

    // All form items are select/pick list fields
    return (
      <>
        <Form.Select
          size="sm"
          value={value}
          onChange={(e) => handleChange(fieldKey, e.target.value)}
          disabled={isDisabled}
          className="dynamic-form-select"
        >
          <option value="">-- Select --</option>
          {item.options &&
            item.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          {item.allow_other && (
            <option value="__other__">Other (specify below)</option>
          )}
        </Form.Select>
        {item.allow_other && value === '__other__' && (
          <Form.Control
            type="text"
            size="sm"
            value={otherTexts[fieldKey] || ''}
            onChange={(e) => handleOtherTextChange(fieldKey, e.target.value)}
            placeholder="Type your preference..."
            disabled={isDisabled}
            className="dynamic-form-input mt-1"
          />
        )}
      </>
    );
  };

  if (!formData || !formData.sections) {
    return null;
  }

  // Count filled items for progress indication
  const totalItems = formData.sections.reduce((acc, s) => acc + s.items.length, 0);
  const filledItems = Object.values(values).filter((v) => v && v !== '').length;

  return (
    <div className="dynamic-form">
      <div className="dynamic-form-header">
        <div className="d-flex align-items-center mb-2">
          <i className="bi bi-ui-checks-grid me-2"></i>
          <span className="fw-semibold">{formData.title}</span>
          {!submitted && (
            <span className="badge bg-secondary ms-auto">
              {filledItems}/{totalItems} filled
            </span>
          )}
          {submitted && (
            <span className="badge bg-success ms-auto">
              <i className="bi bi-check me-1"></i>Submitted
            </span>
          )}
        </div>
        {formData.description && (
          <p className="text-muted mb-2">{formData.description}</p>
        )}
      </div>

      <Form onSubmit={handleSubmit}>
        <Accordion defaultActiveKey={formData.sections.map((_, i) => String(i))} alwaysOpen>
          {formData.sections.map((section, sectionIndex) => (
            <Accordion.Item eventKey={String(sectionIndex)} key={sectionIndex}>
              <Accordion.Header className="dynamic-form-section-header">
                {section.name}
              </Accordion.Header>
              <Accordion.Body className="dynamic-form-section-body">
                {section.items.map((item, itemIndex) => {
                  const fieldKey = keyMap[`${sectionIndex}_${itemIndex}`];
                  return (
                    <div key={fieldKey} className="dynamic-form-item d-flex align-items-center mb-2">
                      <Form.Label className="dynamic-form-label mb-0 me-2">
                        {item.label}
                      </Form.Label>
                      <div className="dynamic-form-input-wrapper flex-grow-1">
                        {renderInput(item, fieldKey)}
                      </div>
                    </div>
                  );
                })}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>

        {!submitted && (
          <div className="dynamic-form-footer mt-3">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={disabled}
              className="dynamic-form-submit"
            >
              <i className="bi bi-check2-circle me-1"></i>
              Submit Selections
            </Button>
            <span className="text-muted small ms-2">
              Leave items blank if no change
            </span>
          </div>
        )}
      </Form>
    </div>
  );
}

export default DynamicForm;
